import { useState, useEffect, useRef } from "react";
import { useAuth, API } from "../App";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import axios from "axios";
import { toast } from "sonner";
import { 
  Plus, 
  Search, 
  Loader2, 
  Trash2, 
  Edit2, 
  Upload, 
  X, 
  ChevronDown, 
  ChevronRight,
  Image as ImageIcon,
  AlertCircle,
  CheckCircle2,
  Clock,
  Folder,
  RefreshCw,
  Cloud
} from "lucide-react";

export default function Gabaritos() {
  const { token } = useAuth();
  const [gabaritos, setGabaritos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedGabarito, setSelectedGabarito] = useState(null);
  const [expandedGabarito, setExpandedGabarito] = useState(null);
  const [stats, setStats] = useState({ total: 0, novos: 0, alterados: 0, imagens: 0 });
  
  // Sync states
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncConfig, setSyncConfig] = useState(null);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    fetchGabaritos();
    fetchSyncConfig();
  }, []);

  // Polling para status da sincronização
  useEffect(() => {
    let interval;
    if (syncing) {
      interval = setInterval(() => {
        fetchSyncStatus();
      }, 2000);
    }
    return () => clearInterval(interval);
  }, [syncing]);

  const fetchSyncConfig = async () => {
    try {
      const response = await axios.get(`${API}/gabaritos/sync/config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSyncConfig(response.data);
    } catch (error) {
      console.error("Erro ao buscar config sync:", error);
    }
  };

  const fetchSyncStatus = async () => {
    try {
      const response = await axios.get(`${API}/gabaritos/sync/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSyncStatus(response.data);
      
      if (!response.data.running && syncing) {
        setSyncing(false);
        fetchGabaritos();
        if (response.data.errors?.length > 0) {
          toast.error(`Sincronização com erros: ${response.data.errors.length} erro(s)`);
        } else if (response.data.message) {
          toast.success(response.data.message);
        }
      }
    } catch (error) {
      console.error("Erro ao buscar status sync:", error);
    }
  };

  const startSync = async () => {
    if (!syncConfig?.google_api_configured) {
      toast.error("Configure a GOOGLE_API_KEY no arquivo .env para usar a sincronização");
      return;
    }
    
    try {
      setSyncing(true);
      await axios.post(`${API}/gabaritos/sync/drive`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.info("Sincronização iniciada...");
      fetchSyncStatus();
    } catch (error) {
      setSyncing(false);
      toast.error(error.response?.data?.detail || "Erro ao iniciar sincronização");
    }
  };

  const fetchGabaritos = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/gabaritos`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGabaritos(response.data.gabaritos || []);
      setStats({
        total: response.data.total_moveis,
        novos: response.data.novos,
        alterados: response.data.alterados,
        imagens: response.data.total_imagens
      });
    } catch (error) {
      toast.error("Erro ao carregar gabaritos");
    } finally {
      setLoading(false);
    }
  };

  const filteredGabaritos = gabaritos.filter(g => {
    const matchesSearch = g.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         g.nome_display.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === "all" || g.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const getStatusBadge = (status) => {
    const styles = {
      novo: { bg: 'bg-blue-100', text: 'text-blue-700', icon: Clock },
      alterado: { bg: 'bg-amber-100', text: 'text-amber-700', icon: AlertCircle },
      ativo: { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle2 },
      inativo: { bg: 'bg-zinc-100', text: 'text-zinc-500', icon: X }
    };
    const style = styles[status] || styles.ativo;
    const Icon = style.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium ${style.bg} ${style.text}`}>
        <Icon size={12} />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  return (
    <Sidebar>
      <div className="p-6 lg:p-8 max-w-7xl" data-testid="gabaritos-page">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="font-chivo font-bold text-2xl tracking-tight">Gabaritos</h1>
            <p className="text-sm text-zinc-500 mt-1">
              Gerencie os móveis e suas imagens de referência
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={startSync}
              disabled={syncing || !syncConfig?.google_api_configured}
              variant="outline"
              data-testid="sync-drive-btn"
              className="rounded-none gap-2"
            >
              {syncing ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Sincronizando...
                </>
              ) : (
                <>
                  <Cloud size={16} />
                  Sincronizar Drive
                </>
              )}
            </Button>
            <Button
              onClick={() => setShowCreateModal(true)}
              data-testid="create-gabarito-btn"
              className="rounded-none bg-zinc-900 hover:bg-zinc-800 text-white gap-2"
            >
              <Plus size={16} />
              Novo Móvel
            </Button>
          </div>
        </div>

        {/* Sync Status */}
        {syncing && syncStatus && (
          <div className="bg-blue-50 border border-blue-200 p-4 mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-blue-800">
                {syncStatus.message || "Sincronizando..."}
              </span>
              <span className="text-xs text-blue-600">
                {syncStatus.progress}/{syncStatus.total}
              </span>
            </div>
            <div className="w-full bg-blue-200 h-2">
              <div 
                className="bg-blue-600 h-2 transition-all"
                style={{ width: `${syncStatus.total > 0 ? (syncStatus.progress / syncStatus.total) * 100 : 0}%` }}
              />
            </div>
            {syncStatus.current_movel && (
              <p className="text-xs text-blue-600 mt-1">
                Processando: {syncStatus.current_movel}
              </p>
            )}
          </div>
        )}

        {/* Sync Config Warning */}
        {syncConfig && !syncConfig.google_api_configured && (
          <div className="bg-amber-50 border border-amber-200 p-4 mb-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-800">
                  Sincronização com Google Drive não configurada
                </p>
                <p className="text-xs text-amber-600 mt-1">
                  Para habilitar a sincronização, adicione sua <code className="bg-amber-100 px-1">GOOGLE_API_KEY</code> no arquivo .env do backend.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white border border-zinc-200 p-4">
            <p className="text-xs text-zinc-500 uppercase tracking-wide">Total de Móveis</p>
            <p className="text-2xl font-bold mt-1">{stats.total}</p>
          </div>
          <div className="bg-white border border-zinc-200 p-4">
            <p className="text-xs text-zinc-500 uppercase tracking-wide">Total de Imagens</p>
            <p className="text-2xl font-bold mt-1">{stats.imagens}</p>
          </div>
          <div className="bg-white border border-zinc-200 p-4">
            <p className="text-xs text-blue-600 uppercase tracking-wide">Novos</p>
            <p className="text-2xl font-bold mt-1 text-blue-600">{stats.novos}</p>
          </div>
          <div className="bg-white border border-zinc-200 p-4">
            <p className="text-xs text-amber-600 uppercase tracking-wide">Alterados</p>
            <p className="text-2xl font-bold mt-1 text-amber-600">{stats.alterados}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar móvel..."
              className="pl-10 rounded-none"
              data-testid="search-gabarito"
            />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-2 border border-zinc-200 text-sm bg-white"
            data-testid="filter-status"
          >
            <option value="all">Todos os status</option>
            <option value="novo">Novos</option>
            <option value="alterado">Alterados</option>
            <option value="ativo">Ativos</option>
            <option value="inativo">Inativos</option>
          </select>
        </div>

        {/* Gabaritos List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-zinc-400" />
          </div>
        ) : filteredGabaritos.length === 0 ? (
          <div className="bg-white border border-zinc-200 p-12 text-center">
            <Folder className="w-12 h-12 text-zinc-300 mx-auto mb-3" />
            <p className="text-zinc-500">Nenhum gabarito encontrado</p>
          </div>
        ) : (
          <div className="space-y-2">
            {filteredGabaritos.map((gabarito) => (
              <GabaritoCard
                key={gabarito.nome}
                gabarito={gabarito}
                isExpanded={expandedGabarito === gabarito.nome}
                onToggle={() => setExpandedGabarito(
                  expandedGabarito === gabarito.nome ? null : gabarito.nome
                )}
                onEdit={() => setSelectedGabarito(gabarito)}
                onRefresh={fetchGabaritos}
                token={token}
                getStatusBadge={getStatusBadge}
              />
            ))}
          </div>
        )}

        {/* Create Modal */}
        {showCreateModal && (
          <CreateGabaritoModal
            onClose={() => setShowCreateModal(false)}
            onSuccess={() => {
              setShowCreateModal(false);
              fetchGabaritos();
            }}
            token={token}
          />
        )}

        {/* Edit Modal */}
        {selectedGabarito && (
          <EditGabaritoModal
            gabarito={selectedGabarito}
            onClose={() => setSelectedGabarito(null)}
            onSuccess={() => {
              setSelectedGabarito(null);
              fetchGabaritos();
            }}
            token={token}
          />
        )}
      </div>
    </Sidebar>
  );
}

// Card de Gabarito
function GabaritoCard({ gabarito, isExpanded, onToggle, onEdit, onRefresh, token, getStatusBadge }) {
  const [images, setImages] = useState(null);
  const [loadingImages, setLoadingImages] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [uploadClassificacao, setUploadClassificacao] = useState(null);

  const loadImages = async () => {
    if (images) return;
    setLoadingImages(true);
    try {
      const response = await axios.get(`${API}/gabaritos/${gabarito.nome}/images`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setImages(response.data.images);
    } catch (error) {
      toast.error("Erro ao carregar imagens");
    } finally {
      setLoadingImages(false);
    }
  };

  useEffect(() => {
    if (isExpanded) {
      loadImages();
    }
  }, [isExpanded]);

  const handleUpload = async (e, classificacao) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      await axios.post(
        `${API}/gabaritos/${gabarito.nome}/images/${classificacao}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      toast.success("Imagem adicionada!");
      setImages(null);
      loadImages();
      onRefresh();
    } catch (error) {
      toast.error("Erro ao fazer upload");
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeleteImage = async (classificacao, filename) => {
    if (!window.confirm("Excluir esta imagem?")) return;

    try {
      await axios.delete(
        `${API}/gabaritos/${gabarito.nome}/images/${classificacao}/${filename}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Imagem removida");
      setImages(null);
      loadImages();
      onRefresh();
    } catch (error) {
      toast.error("Erro ao remover imagem");
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await axios.post(
        `${API}/gabaritos/${gabarito.nome}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Status alterado para "${newStatus}"`);
      onRefresh();
    } catch (error) {
      toast.error("Erro ao alterar status");
    }
  };

  const classificacoes = ['Boa', 'Ruim', 'Vazia', 'Critica'];
  const classColors = {
    'Boa': 'border-green-500 bg-green-50',
    'Ruim': 'border-orange-500 bg-orange-50',
    'Vazia': 'border-yellow-500 bg-yellow-50',
    'Critica': 'border-red-500 bg-red-50'
  };

  return (
    <div className="bg-white border border-zinc-200">
      {/* Header */}
      <div
        className="flex items-center gap-4 p-4 cursor-pointer hover:bg-zinc-50"
        onClick={onToggle}
      >
        <button className="text-zinc-400">
          {isExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
        </button>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3">
            <h3 className="font-medium truncate">{gabarito.nome_display}</h3>
            {getStatusBadge(gabarito.status)}
          </div>
          <p className="text-xs text-zinc-500 mt-0.5">
            {gabarito.total_imagens} imagens • 
            Boa: {gabarito.classificacoes?.Boa || 0} | 
            Ruim: {gabarito.classificacoes?.Ruim || 0} | 
            Vazia: {gabarito.classificacoes?.Vazia || 0} | 
            Crítica: {gabarito.classificacoes?.Critica || 0}
          </p>
        </div>

        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
          <select
            value={gabarito.status}
            onChange={(e) => handleStatusChange(e.target.value)}
            className="px-2 py-1 text-xs border border-zinc-200 bg-white"
          >
            <option value="novo">Novo</option>
            <option value="alterado">Alterado</option>
            <option value="ativo">Ativo</option>
            <option value="inativo">Inativo</option>
          </select>
          <Button
            variant="ghost"
            size="sm"
            onClick={onEdit}
            className="h-8 w-8 p-0"
          >
            <Edit2 size={14} />
          </Button>
        </div>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="border-t border-zinc-200 p-4">
          {loadingImages ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin text-zinc-400" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {classificacoes.map((classificacao) => (
                <div key={classificacao} className={`border-2 ${classColors[classificacao]}`}>
                  <div className="p-2 text-center border-b">
                    <p className="text-xs font-bold uppercase">{classificacao}</p>
                    <p className="text-xs text-zinc-500">
                      {images?.[classificacao]?.length || 0} imagens
                    </p>
                  </div>
                  
                  {/* Images Grid */}
                  <div className="p-2 space-y-2 max-h-64 overflow-y-auto">
                    {images?.[classificacao]?.map((img, idx) => (
                      <div key={idx} className="relative group">
                        <img
                          src={img.data}
                          alt={img.filename}
                          className="w-full h-20 object-cover"
                        />
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                          <button
                            onClick={() => handleDeleteImage(classificacao, img.filename)}
                            className="p-1 bg-red-500 text-white rounded"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                        <p className="text-xs text-zinc-500 truncate mt-1">{img.filename}</p>
                      </div>
                    ))}
                  </div>

                  {/* Upload Button */}
                  <div className="p-2 border-t">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleUpload(e, classificacao)}
                      className="hidden"
                      id={`upload-${gabarito.nome}-${classificacao}`}
                      disabled={uploading}
                    />
                    <label
                      htmlFor={`upload-${gabarito.nome}-${classificacao}`}
                      className={`flex items-center justify-center gap-1 w-full py-2 text-xs border border-dashed border-zinc-300 cursor-pointer hover:bg-zinc-50 ${uploading ? 'opacity-50' : ''}`}
                    >
                      {uploading ? (
                        <Loader2 size={14} className="animate-spin" />
                      ) : (
                        <>
                          <Upload size={14} />
                          Adicionar
                        </>
                      )}
                    </label>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Modal de Criação
function CreateGabaritoModal({ onClose, onSuccess, token }) {
  const [nome, setNome] = useState("");
  const [status, setStatus] = useState("novo");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!nome.trim()) {
      toast.error("Digite o nome do móvel");
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${API}/gabaritos`,
        { nome: nome.trim(), status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Móvel criado com sucesso!");
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao criar móvel");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white w-full max-w-md">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="font-bold text-lg">Novo Móvel</h2>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-600">
            <X size={20} />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <Label className="text-sm">Nome do Móvel</Label>
            <Input
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              placeholder="Ex: Geladeira_Bebidas"
              className="mt-1 rounded-none"
              data-testid="input-movel-nome"
            />
            <p className="text-xs text-zinc-500 mt-1">
              Use underscores (_) para separar palavras
            </p>
          </div>

          <div>
            <Label className="text-sm">Status Inicial</Label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full mt-1 px-3 py-2 border border-zinc-200 text-sm"
            >
              <option value="novo">Novo</option>
              <option value="ativo">Ativo</option>
            </select>
          </div>

          <div className="flex gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1 rounded-none"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="flex-1 rounded-none bg-zinc-900 text-white"
              data-testid="submit-create-movel"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Criar Móvel"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Modal de Edição
function EditGabaritoModal({ gabarito, onClose, onSuccess, token }) {
  const [novoNome, setNovoNome] = useState(gabarito.nome);
  const [status, setStatus] = useState(gabarito.status);
  const [notas, setNotas] = useState(gabarito.notas || "");
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    setLoading(true);
    try {
      // Atualizar nome se mudou
      if (novoNome !== gabarito.nome) {
        await axios.put(
          `${API}/gabaritos/${gabarito.nome}`,
          { novo_nome: novoNome, status },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      // Atualizar status e notas
      await axios.post(
        `${API}/gabaritos/${novoNome || gabarito.nome}/status`,
        { status, notas },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success("Móvel atualizado!");
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao atualizar");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Excluir o móvel "${gabarito.nome}" e todas suas imagens?`)) return;

    setDeleting(true);
    try {
      await axios.delete(`${API}/gabaritos/${gabarito.nome}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Móvel excluído");
      onSuccess();
    } catch (error) {
      toast.error("Erro ao excluir");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white w-full max-w-md">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="font-bold text-lg">Editar Móvel</h2>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-600">
            <X size={20} />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <Label className="text-sm">Nome do Móvel</Label>
            <Input
              value={novoNome}
              onChange={(e) => setNovoNome(e.target.value)}
              className="mt-1 rounded-none"
            />
          </div>

          <div>
            <Label className="text-sm">Status</Label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full mt-1 px-3 py-2 border border-zinc-200 text-sm"
            >
              <option value="novo">Novo</option>
              <option value="alterado">Alterado</option>
              <option value="ativo">Ativo</option>
              <option value="inativo">Inativo</option>
            </select>
          </div>

          <div>
            <Label className="text-sm">Notas</Label>
            <Textarea
              value={notas}
              onChange={(e) => setNotas(e.target.value)}
              placeholder="Observações sobre este móvel..."
              className="mt-1 rounded-none"
              rows={2}
            />
          </div>

          <div className="flex gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleDelete}
              disabled={deleting}
              className="rounded-none text-red-600 hover:bg-red-50 hover:border-red-200"
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 size={16} />}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1 rounded-none"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="flex-1 rounded-none bg-zinc-900 text-white"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Salvar"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
